import { faCircleExclamation, faEye, faEyeSlash, faLaptop, faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Table from '@sharedComponents/Table';
import Tag from '@sharedComponents/Tag';
import Tooltip from '@sharedComponents/Tooltip';
import Typography from '@sharedComponents/Typography';
import { useTranslation } from 'react-i18next';

import { RESTRICTED } from '../accessRules';
import OverrideBadge from '../OverrideBadge';
import { isEmptyRow, overriddenLanguages, targetKey, targetOf } from '../overrideRules';
import TargetLabel from '../TargetLabel';

import * as style from './style.module.css';

const noop = () => {};

/**
 * Table of the override rows of a collection, one row per target in the order of `rows`: its label, the badge
 * of its title and of its description, its display (public collection only) and a bin. The pencil next to the
 * label opens the sub-clients and platforms of a client row (`onOpenSubTargets(row)`), or the transfer of a
 * platform row to a client (`onOpenTransfer(row)`). The pencils of a row show when the row is hovered or holds
 * the focus. `languages` are the catalogue languages, which the badges count against; the badge of an
 * overridden text and the warning of a row without override take the focus, which shows their tooltip.
 * `disabled`, or the `kind:uuid` key of the row's target in the `pendingKeys` array, disables the buttons of a
 * row; the transfer of a platform row is also disabled by the key of its client or of the parent of its client.
 * The table sits in a frame and scrolls inside it when its columns do not fit. Without a row, a message replaces
 * the table and its legend.
 */
const OverrideTable = ({
    visibility,
    rows = [],
    languages = [],
    disabled = false,
    pendingKeys = [],
    onEditField = noop,
    onToggleVisible = noop,
    onRemove = noop,
    onOpenSubTargets = noop,
    onOpenTransfer = noop,
}) => {
    const { t } = useTranslation('bo');
    const restricted = visibility === RESTRICTED;

    if (rows.length === 0) {
        return restricted ? (
            <Typography color="warning-primary">{t('No authorized client: the collection is visible to no one.')}</Typography>
        ) : (
            <Typography color="neutral-secondary">
                {t('No overridden client: the collection is shown as it is defined for every client.')}
            </Typography>
        );
    }

    const lockedOf = (row) => disabled || pendingKeys.includes(targetKey(targetOf(row)));
    // The transfer of a platform row replaces the row of its client or of its parent: it waits for theirs too.
    const transferLockedOf = (row) =>
        lockedOf(row) ||
        [row.client, row.client.parent].some((client) => client && pendingKeys.includes(targetKey({ kind: 'client', uuid: client.uuid })));
    const warning = t('No override configured: this target sees the collection as it is defined. You can delete this row.');

    const iconButton = (row, icon, label, action, locked = lockedOf(row)) => (
        <Button ghost icon={icon} title={label} aria-label={label} disabled={locked} action={action} />
    );
    const pencil = (row, label, action, locked) => (
        <FlexBox className={style.pencil}>{iconButton(row, faPen, label, action, locked)}</FlexBox>
    );

    const editLabel = (field, overridden, name) => {
        if (field === 'title') {
            return overridden ? t('Edit the title for {{name}}', { name }) : t('Customize the title for {{name}}', { name });
        }
        return overridden ? t('Edit the description for {{name}}', { name }) : t('Customize the description for {{name}}', { name });
    };

    const clientCell = (row) => (
        <TargetLabel row={row}>
            {!restricted && isEmptyRow(row) && (
                <Tooltip text={warning} popupIcon={false} position="top">
                    <FlexBox role="img" tabIndex={0} aria-label={warning} className={style.warningIcon}>
                        <FontAwesomeIcon icon={faCircleExclamation} />
                    </FlexBox>
                </Tooltip>
            )}
            {row.platform
                ? pencil(
                      row,
                      t('Transfer the override of {{name}} to a client', { name: targetOf(row).name }),
                      () => onOpenTransfer(row),
                      transferLockedOf(row),
                  )
                : pencil(row, t('Manage the sub-clients and platforms of {{name}}', { name: targetOf(row).name }), () =>
                      onOpenSubTargets(row),
                  )}
        </TargetLabel>
    );

    const fieldCell = (row, field) => {
        const overridden = overriddenLanguages(row[field]).length > 0;
        return (
            <FlexBox alignItems="center" gap="xxs">
                <OverrideBadge text={row[field]} total={languages.length} withLanguages />
                {pencil(row, editLabel(field, overridden, targetOf(row).name), () => onEditField(row, field))}
            </FlexBox>
        );
    };

    // A hidden row shows its crossed eye in a danger button, a visible row its eye in a ghost button.
    const displayCell = (row) => {
        const { name } = targetOf(row);
        const hidden = row.visible === false;
        const label = hidden ? t('Show the collection for {{name}}', { name }) : t('Hide the collection for {{name}}', { name });
        const toggle = () => onToggleVisible(row);
        return (
            <FlexBox alignItems="center" gap="xxs" className={hidden ? style.hidden : undefined}>
                {hidden ? (
                    <Button theme="danger" icon={faEyeSlash} title={label} aria-label={label} disabled={lockedOf(row)} action={toggle} />
                ) : (
                    iconButton(row, faEye, label, toggle)
                )}
                <Typography
                    span
                    fontSize="xs"
                    fontWeight={hidden ? 'medium' : 'regular'}
                    color={hidden ? 'error-primary' : 'neutral-secondary'}
                >
                    {hidden ? t('Hidden') : t('Visible')}
                </Typography>
            </FlexBox>
        );
    };

    const removeCell = (row) => {
        const { name } = targetOf(row);
        const label = restricted ? t('Remove the access of {{name}}', { name }) : t('Remove {{name}} from the list', { name });
        return iconButton(row, faTrash, label, () => onRemove(row));
    };

    // Classes of the header and body cells of a column, `width` giving its width.
    const cellClasses = (width) => ({ thClassName: `${style.head} ${width}`, tdClassName: `${style.cell} ${width}` });

    const columns = [
        { key: 'client', label: t('Client'), pinned: true, fill: true, ...cellClasses(style.clientCol), render: clientCell },
        { key: 'title', label: t('Title'), pinned: true, ...cellClasses(style.textCol), render: (row) => fieldCell(row, 'title') },
        {
            key: 'description',
            label: t('Description'),
            pinned: true,
            ...cellClasses(style.textCol),
            render: (row) => fieldCell(row, 'description'),
        },
        ...(restricted
            ? []
            : [{ key: 'display', label: t('Display'), pinned: true, ...cellClasses(style.displayCol), render: displayCell }]),
        { key: 'remove', ariaLabel: t('Remove'), pinned: true, ...cellClasses(style.removeCol), render: removeCell },
    ];

    const legendItem = (mark, text) => (
        <FlexBox alignItems="center" gap="xs">
            {mark}
            <Typography span fontSize="xs" color="neutral-secondary">
                {text}
            </Typography>
        </FlexBox>
    );

    return (
        <FlexBox flexDirection="column" gap="s">
            <FlexBox flexDirection="column" className={style.frame}>
                <Table columns={columns} items={rows} getRowKey={(row) => row.uuid} />
            </FlexBox>
            <FlexBox alignItems="center" gap="m" flexWrap="wrap" additionnalStyles={{ rowGap: 'var(--space-xs)' }}>
                {legendItem(
                    <Tag color="neutral" size="xs" fontSize="xs">
                        {t('Inherited')}
                    </Tag>,
                    t('value of the collection'),
                )}
                {legendItem(
                    <Tag color="lavender" size="xs" fontSize="xs">
                        {t('Overridden · X/Y')}
                    </Tag>,
                    t('value specific to the target, and overridden languages'),
                )}
                {!restricted &&
                    legendItem(<FontAwesomeIcon icon={faEyeSlash} className={style.hiddenIcon} />, t('collection hidden for this target'))}
                {legendItem(<FontAwesomeIcon icon={faLaptop} className={style.mutedIcon} />, t('override of a single platform'))}
                {!restricted &&
                    legendItem(
                        <FontAwesomeIcon icon={faCircleExclamation} className={style.warningIcon} />,
                        t('target listed without override'),
                    )}
                <Typography span fontSize="xs" color="neutral-tertiary">
                    {restricted
                        ? t(
                              'Hover over a cell to edit it. The bin removes the access: without an overridden text, the row disappears and the row of its client applies again; with a text, it moves to the block of overrides kept without access.',
                          )
                        : t('Hover over a cell to edit it.')}
                </Typography>
            </FlexBox>
        </FlexBox>
    );
};

export default OverrideTable;
